import {
    ICartItemData,
    ICartItemsByFare,
    ICartItemsByGroup,
    ICartItemsByProduct,
} from "@interfaces";
import _ from "lodash";
import { useState, useMemo, useEffect } from "react";
import moment from "moment";
import { PointCommon } from "@components";
import { breakpoint, theme } from "@utilities";
import styled from "styled-components";
import { ItemsByProductHeader } from "./ItemByProductHeader";
import {
    buildItemsForNewQuantity,
    formatBookingDatesByProductClass,
    getTotalRRP,
    groupCartItemsByProduct,
    transformCartItemsForQuote,
} from "../checkoutHelpers";
import { CheckoutItemsActions } from "./CheckoutActions";
import { useSharedByEmail } from "../checkoutSharedHelper";
import { WarnToast } from "src/Components/toasts/WarnToast";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { useCheckout } from "../CheckoutContext";
import { useAppDispatch, useAppSelector, useIsLoggedIn, selectAccount } from "@redux";
import { useParams, useSearchParams, useLocation, useNavigate } from "react-router-dom";
import {
    loadSharedQuotes,
    createNewQuoteAndReload,
    addItemToExistingQuoteAndReload,
    loadQuotes,
} from "src/Redux/Slices/allQuotes";
import { PATH_BOOK_NOW, PATH_HOME, PATH_QUOTES } from "@routes";
import { BorderButton } from "src/Module/TourCheckAvail/BookNowButton";
import { useDynamicBottomHeight } from "@utilities";
import { createNewQuoteModal, addToExistingQuoteModal } from "src/Module/Modals";
import { isBookingDateInPast } from "../bookingDateHelpers";

interface ICartItemByGroupProps extends ICartItemsByGroup {
    productClass: string;
    onItemsChange: (items: ICartItemData[]) => void;
    handleDeleteItems?: (cartItemIds: string[]) => void;
    originalItems: ICartItemData[];
}

const checkItemExpired = (item: ICartItemData) => {
    try {
        const now = new Date();
        const today = moment().format("YYYY-MM-DD");

        const parsedDate = moment(
            item.booking_date,
            ["DD-MMM-YYYY", "YYYY-MM-DD", moment.ISO_8601],
            true,
        );
        if (!parsedDate.isValid()) return false;

        const bookingDateStr = parsedDate.format("YYYY-MM-DD");
        const currentDateStr = moment().format("YYYY-MM-DD");

        if (bookingDateStr < currentDateStr) return true;

        if (bookingDateStr === today && item.booking_details?.bookingTimes?.[0]?.BookingTime) {
            const [time, period] = item.booking_details.bookingTimes[0].BookingTime.split(" ");
            const [hoursStr, minutesStr] = time.split(":");
            let hours = parseInt(hoursStr);
            const minutes = parseInt(minutesStr);

            if (period === "PM" && hours !== 12) hours += 12;
            else if (period === "AM" && hours === 12) hours = 0;

            const bookingTime = new Date();
            bookingTime.setHours(hours, minutes, 0, 0);
            return bookingTime <= now;
        }

        return false;
    } catch {
        return false;
    }
};

const checkItemInvalid = (item: ICartItemData) => {
    // Check all validation issues including product/availability
    if (checkItemExpired(item)) return true;
    if (item.is_product_latest === false) return true;
    if (item.is_availability_latest === false) return true;
    return false;
};

const checkItemDateExpired = (item: ICartItemData) => isBookingDateInPast(item.booking_date);

const checkItemTimeExpired = (item: ICartItemData) => {
    try {
        const now = new Date();
        const today = moment().format("YYYY-MM-DD");
        const parsedDate = moment(
            item.booking_date,
            ["DD-MMM-YYYY", "YYYY-MM-DD", moment.ISO_8601],
            true,
        );
        if (!parsedDate.isValid()) return false;
        const itemBookingDate = parsedDate.format("YYYY-MM-DD");

        if (itemBookingDate === today && item.booking_details?.bookingTimes?.[0]?.BookingTime) {
            const [time, period] = item.booking_details.bookingTimes[0].BookingTime.split(" ");
            const [hoursStr, minutesStr] = time.split(":");
            let hours = parseInt(hoursStr);
            const minutes = parseInt(minutesStr);

            if (period === "PM" && hours !== 12) hours += 12;
            else if (period === "AM" && hours === 12) hours = 0;

            const bookingTime = new Date();
            bookingTime.setHours(hours, minutes, 0, 0);
            return bookingTime <= now;
        }
        return false;
    } catch {
        return false;
    }
};

export const CartItemGroupComponent = ({
    items,
    originalItems,
    productClass,
    onItemsChange,
    handleDeleteItems,
}: ICartItemByGroupProps) => {
    const rawQuantity = useMemo(() => items[0]?.booking_quantity || 0, [items]);
    const PpdId = items[0]?.product_price_details_id;
    const price = getTotalRRP(items);
    const matchingFare = items[0]?.product?.json?.faresprices?.find(
        (fare) => Number(fare.productPricesDetailsId) === Number(PpdId),
    );
    const numPax = matchingFare ? Number(matchingFare.numPax) || 1 : 1;
    const quantity = rawQuantity / numPax;
    const maxAvailable = Math.max(...items.map((item) => item.availability.NumAvailable)) / numPax;

    const [searchParams] = useSearchParams();
    const isPending = searchParams.get("isPending") === "true";

    const isItemExpired = useMemo(() => items.some(checkItemExpired), [items]);

    useEffect(() => {
        if (_.uniq(items.map((i) => i.booking_quantity)).length > 1) {
            console.warn(`Different quantities under same group`, items);
        }
    }, [items]);

    const handleQuantityUpdate = (newQuantity: number) => {
        if (newQuantity < 1) {
            if (handleDeleteItems) {
                handleDeleteItems(
                    items.filter((i) => !_.isNil(i) && !_.isNil(i.id)).map((i) => i.id.toString()),
                );
            } else {
                WarnToast({ Message: "Minimum quantity reached" });
            }
            return;
        }
        if (newQuantity > maxAvailable) {
            WarnToast({ Message: `Not allowed than max available` });
            return;
        }
        onItemsChange(buildItemsForNewQuantity(items, newQuantity, originalItems));
    };

    return (
        <CartItemRowContainer isExpired={isItemExpired}>
            <DateText>
                {formatBookingDatesByProductClass(
                    items.map((i) => i.booking_date),
                    productClass,
                )}
            </DateText>
            <PriceContainerDiv>
                <div className="price">
                    <p>Price</p> {price && <span>${price.totalRRP.toFixed(2)}</span>}
                </div>
                <div className="quantity">
                    Qty
                    <PointCommon
                        disabled={isPending}
                        type="button"
                        onClick={() => handleQuantityUpdate(quantity - 1)}
                    >
                        <svg width="10" height="2" viewBox="0 0 10 2" fill="none">
                            <path
                                d="M1 1H9"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                            />
                        </svg>
                    </PointCommon>
                    <span>{quantity}</span>
                    <PointCommon
                        disabled={isPending}
                        type="button"
                        onClick={() => handleQuantityUpdate(quantity + 1)}
                    >
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path
                                d="M5 1V9M1 5H9"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                            />
                        </svg>
                    </PointCommon>
                </div>
            </PriceContainerDiv>
        </CartItemRowContainer>
    );
};

const CartItemRowContainer = styled.div<{ isExpired?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 10px;
    opacity: ${({ isExpired }) => (isExpired ? 0.5 : 1)};
`;

const PriceContainerDiv = styled.div`
    border: 1px solid #c3c9d0;
    border-radius: ${theme.borderRadius.medium};
    display: flex;
    justify-content: space-between;
    padding: 8px 14px;
    align-items: center;

    .price {
        display: flex;
        flex-direction: column;
        color: #6e7375;
        font-size: 12px;
        font-family: Plus Jakarta Sans;
        span {
            color: #000;
            font-size: 18px;
        }
    }

    .quantity {
        color: #6e7375;
        font-size: 12px;
        display: flex;
        align-items: center;
        gap: 10px;
        span {
            font-size: 20px;
            color: #1f2937;
            width: 18px;
            text-align: center;
        }
        button {
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }
`;

const DateText = styled.p`
    font-size: 12px;
    font-weight: 600;
    font-family: Plus Jakarta Sans;
    color: #232b2b;
    margin: 4px 0px 8px 0px;
    text-decoration: underline;
    text-underline-offset: 3px;
`;

interface ICartItemsByFareProps extends ICartItemsByFare {
    productClass: string;
    onItemsChange: (items: ICartItemData[]) => void;
    handleDeleteItems?: (cartItemIds: string[]) => void;
    originalItemsByGroup: ICartItemsByGroup[];
}

const CartItemsByFareComponent = (props: ICartItemsByFareProps) => {
    const isAllItemsExpired = useMemo(
        () => props.itemsByGroup.every((group) => group.items.every(checkItemExpired)),
        [props.itemsByGroup],
    );

    return (
        <div>
            <div className="fareName" style={{ opacity: isAllItemsExpired ? 0.5 : 1 }}>
                {props.name}
            </div>
            {props.itemsByGroup.map((itemByGroup, groupIndex) => (
                <CartItemGroupComponent
                    key={groupIndex}
                    group_id={itemByGroup.group_id}
                    items={itemByGroup.items}
                    originalItems={props.originalItemsByGroup[groupIndex]?.items || []}
                    productClass={props.productClass}
                    onItemsChange={props.onItemsChange}
                    handleDeleteItems={props.handleDeleteItems}
                />
            ))}
        </div>
    );
};

interface ICartItemByProductProps extends ICartItemsByProduct {
    handleDeleteProduct?: (productId: string) => void;
    onItemsChange: (items: ICartItemData[]) => void;
    handleDeleteItems?: (cartItemIds: string[]) => void;
    originalItemsByFare: ICartItemsByFare[];
    supplierName: string;
    is_product_latest?: boolean;
    is_availability_latest?: boolean;
    isAnyProductInvalid?: boolean;
}

const CartItemsByProduct = (props: ICartItemByProductProps) => {
    // Use efficient nested some() for validation (short-circuits on first match)
    const isDateExpiredMemo = useMemo(
        () =>
            props.itemsByFare.some((fare) =>
                fare.itemsByGroup.some((group) => group.items.some(checkItemDateExpired)),
            ),
        [props.itemsByFare],
    );

    const hasTimeExpiredMemo = useMemo(
        () =>
            props.itemsByFare.some((fare) =>
                fare.itemsByGroup.some((group) => group.items.some(checkItemTimeExpired)),
            ),
        [props.itemsByFare],
    );

    const isProductExpired =
        isDateExpiredMemo ||
        hasTimeExpiredMemo ||
        props.is_product_latest === false ||
        props.is_availability_latest === false;

    const noticeMessage = useMemo(() => {
        if (isDateExpiredMemo) return "This booking date has passed";
        if (hasTimeExpiredMemo) return "This booking time has passed";
        if (props.is_product_latest === false) return "This item is no longer available";
        if (props.is_availability_latest === false) return "This item is no longer available";
        return null;
    }, [
        isDateExpiredMemo,
        hasTimeExpiredMemo,
        props.is_product_latest,
        props.is_availability_latest,
    ]);

    // No-op function to avoid linter error
    const noOp = () => {
        WarnToast({ Message: "Cannot modify expired or outdated product" });
    };

    return (
        <Info className="infoWrapper">
            <div className="info">
                {noticeMessage && (
                    <ExpiredNotice>
                        <TriangleWarning>
                            <img src="/images/toast/Expire.svg" alt="warning" />
                        </TriangleWarning>
                        <ExpiredText>{noticeMessage}</ExpiredText>
                    </ExpiredNotice>
                )}
                <ItemsByProductHeader
                    {...props}
                    productId={props.productId}
                    isExpired={isProductExpired}
                    {...(props.handleDeleteProduct && {
                        onDelete: () => props.handleDeleteProduct!(props.productId.toString()),
                        showDeleteButton: true,
                    })}
                />
                <div>
                    <Border />
                    {props.itemsByFare.map((itemsByFare, fareIndex) => (
                        <CartItemsByFareComponent
                            key={fareIndex}
                            id={itemsByFare.id}
                            name={itemsByFare.name}
                            productClass={props.productClass}
                            itemsByGroup={itemsByFare.itemsByGroup}
                            originalItemsByGroup={
                                props.originalItemsByFare[fareIndex]?.itemsByGroup || []
                            }
                            onItemsChange={
                                isProductExpired || props.isAnyProductInvalid
                                    ? noOp
                                    : props.onItemsChange
                            }
                            handleDeleteItems={props.handleDeleteItems}
                            bookingSystem={itemsByFare.bookingSystem}
                        />
                    ))}
                </div>
            </div>
        </Info>
    );
};

const ExpiredNotice = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0px;
    gap: 4px;
    width: 225px;
    height: 20px;
    flex: none;
    order: 0;
    flex-grow: 0;
    margin-bottom: 8px;
`;

const TriangleWarning = styled.div`
    width: 24px;
    height: 24px;
    flex: none;
    order: 0;
    flex-grow: 0;
`;

const ExpiredText = styled.p`
    width: 197px;
    height: 17px;
    font-family: "Plus Jakarta Sans";
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 120%;
    display: flex;
    align-items: center;
    color: #db340b;
    flex: none;
    order: 1;
    flex-grow: 0;
    margin: 0;
`;
interface ICheckoutStep1Props {
    handleDeleteItems?: (cartItemIds: string[]) => void;
    handleDeleteProduct?: (productId: string) => void;
    onItemsSave: (items: ICartItemData[], shouldGoToNextStep: boolean) => Promise<void>;
    handleDeleteCart?: () => void;
}

export const CheckoutStep1 = ({
    handleDeleteItems,
    handleDeleteProduct,
    onItemsSave,
    handleDeleteCart,
}: ICheckoutStep1Props) => {
    const { items, setItems } = useCheckout();
    const itemsByProduct = useMemo(() => groupCartItemsByProduct(items), [items]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSavingToQuote, setIsSavingToQuote] = useState(false);
    const bottomContainerRef = useDynamicBottomHeight([isSubmitting], 50);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { quoteId: quoteIdFromParam } = useParams<{ quoteId: string }>();
    const quoteId = quoteIdFromParam!;
    const [searchParams] = useSearchParams();
    const isPending = searchParams.get("isPending") === "true";
    const isBookNow = window.location.pathname.includes(PATH_BOOK_NOW);
    const isQuotesPage = location.pathname.includes(PATH_QUOTES);
    const sharedToMeQuotes = useAppSelector((state) => state.allQuotes.sharedQuotes);
    const sharedBy = sharedToMeQuotes.find((q) => q.id === parseInt(quoteId))?.shared_by;
    const isLoggedIn = useIsLoggedIn();
    const { metadata } = useAppSelector(selectAccount);
    const isAgent = !!(metadata?.is_commission_agent || metadata?.is_points_agent);

    const sharedByEmail = useSharedByEmail();

    useEffect(() => {
        // Only load shared quotes if user is logged in
        if (isQuotesPage && isLoggedIn && location.pathname === PATH_QUOTES) {
            dispatch(loadSharedQuotes());
        }
    }, [dispatch, isQuotesPage, isLoggedIn, location.pathname]);

    /**
     * Check if product pricing is outdated/no longer available
     * - Compares current product price details ID with latest available fares
     * - Returns true if the selected fare is no longer in the current pricing structure
     * - Only applies to cart items (not book-now flow)
     */
    const checkIsOutdated = (product: ICartItemsByProduct) => {
        return (
            !isBookNow &&
            !product.itemsByFare[0]?.itemsByGroup[0]?.items[0]?.product?.json?.faresprices?.some(
                (fare) =>
                    fare.productPricesDetailsId ===
                    product.itemsByFare[0]?.itemsByGroup[0]?.items[0]?.product_price_details_id,
            )
        );
    };

    /**
     * Check if any product in cart has validation issues:
     * 1. Expired items - booking date has passed
     * 2. Outdated pricing - selected fare no longer available
     * 3. Invalid availability - product no longer bookable
     */
    const isAnyProductInvalid = useMemo(() => {
        return itemsByProduct.some(
            (product) =>
                // Check for expired items (past booking dates)
                product.itemsByFare.some((fare) =>
                    fare.itemsByGroup.some((group) => group.items.some(checkItemInvalid)),
                ) ||
                // Check for outdated pricing (fare no longer available)
                checkIsOutdated(product),
        );
    }, [itemsByProduct]);

    /**
     * Count different types of invalid products for warning messages:
     * - expiredCount: Products with past booking dates
     * - outdatedCount: Products with unavailable pricing/fares
     * - pastTimeCount: Products with past booking times (currently unused)
     */
    const { expiredCount, outdatedCount, pastTimeCount } = useMemo(() => {
        let expired = 0;
        let outdated = 0;

        itemsByProduct.forEach((product) => {
            // Check if product has any expired booking dates
            const hasExpiredItems = product.itemsByFare.some((fare) =>
                fare.itemsByGroup.some((group) => group.items.some(checkItemInvalid)),
            );
            // Check if product pricing is no longer available
            const isOutdated = checkIsOutdated(product);

            if (hasExpiredItems) expired++;
            if (isOutdated) outdated++;
        });

        return { expiredCount: expired, outdatedCount: outdated, pastTimeCount: 0 };
    }, [itemsByProduct]);

    /**
     * Generate user-friendly warning message based on validation issues:
     * - Combines multiple issue types into a single message
     * - Handles singular/plural grammar correctly
     * - Provides clear action guidance (remove/update items)
     */
    const topWarningMessage = useMemo(() => {
        if (expiredCount > 0 && outdatedCount > 0) {
            return `${expiredCount} item${expiredCount > 1 ? "s have" : " has"} expired and ${outdatedCount} item${outdatedCount > 1 ? "s are" : " is"} no longer available. Please remove or update these items to continue.`;
        } else if (expiredCount > 0) {
            return `${expiredCount} item${expiredCount > 1 ? "s have" : " has"} expired. Please remove these items to continue.`;
        } else if (outdatedCount > 0) {
            return `${outdatedCount} item${outdatedCount > 1 ? "s are" : " is"} no longer available. Please remove these items to continue.`;
        } else if (pastTimeCount > 0) {
            return `${pastTimeCount} item${pastTimeCount > 1 ? "s have" : " has"} past booking times. Please remove these items to continue.`;
        }
        return null;
    }, [expiredCount, outdatedCount, pastTimeCount]);

    const handleSaveToQuote = async () => {
        if (items.length === 0) {
            ErrorToast({ Message: "No items to save" });
            return;
        }
        try {
            setIsSavingToQuote(true);
            const addItemBases = transformCartItemsForQuote(items);
            const firstItem = addItemBases[0];
            const title = await createNewQuoteModal();
            if (!title) {
                setIsSavingToQuote(false);
                return;
            }
            await dispatch(createNewQuoteAndReload({ ...firstItem, quoteTitle: title })).unwrap();
            if (addItemBases.length > 1) {
                await dispatch(loadQuotes());
                const allQuotes = (await import("@redux")).store.getState().allQuotes;
                const createdQuote = (
                    allQuotes.allQuotes?.length > 0 ? allQuotes.allQuotes : allQuotes.quotes
                ).find((q: { title: string }) => q.title.toLowerCase() === title.toLowerCase());
                if (createdQuote) {
                    const remainingItems = addItemBases.slice(1);
                    await Promise.allSettled(
                        remainingItems.map((itemBase) =>
                            dispatch(
                                addItemToExistingQuoteAndReload({
                                    ...itemBase,
                                    quoteId: String(createdQuote.id),
                                }),
                            ).unwrap(),
                        ),
                    );
                }
            }
            navigate(PATH_QUOTES);
        } catch (error: any) {
            console.error("Error saving to quote:", error);
            ErrorToast({ Message: error?.title || error?.message || "Failed to save to quote" });
        } finally {
            setIsSavingToQuote(false);
        }
    };

    const handleAddToExistingQuote = async () => {
        if (items.length === 0) {
            ErrorToast({ Message: "No items to save" });
            return;
        }
        try {
            setIsSavingToQuote(true);
            await dispatch(loadQuotes());
            const selectedQuote = await addToExistingQuoteModal();
            if (!selectedQuote) {
                setIsSavingToQuote(false);
                return;
            }
            const addItemBases = transformCartItemsForQuote(items);
            await Promise.allSettled(
                addItemBases.map((itemBase) =>
                    dispatch(
                        addItemToExistingQuoteAndReload({
                            ...itemBase,
                            quoteId: String(selectedQuote.id),
                        }),
                    ).unwrap(),
                ),
            );
            navigate(PATH_QUOTES);
        } catch (error: any) {
            console.error("Error adding to existing quote:", error);
            ErrorToast({ Message: error?.title || error?.message || "Failed to add to quote" });
        } finally {
            setIsSavingToQuote(false);
        }
    };

    const handleGroupItemsChange = (updatedItems: ICartItemData[]) => {
        setItems(
            items.map((cartItem) => {
                const updateItem = updatedItems.find((item) => item.id === cartItem.id);
                if (updateItem) {
                    return updateItem;
                }
                return cartItem;
            }),
        );
    };

    return (
        <Container>
            <div className="cartWrapper">
                {((isPending && sharedBy) || (!isPending && !sharedBy && sharedByEmail)) && (
                    <SharedByBanner>
                        shared By : <span>{sharedBy || sharedByEmail}</span>
                    </SharedByBanner>
                )}
                {handleDeleteCart && (
                    <RemoveAll disabled={isPending} onClick={handleDeleteCart}>
                        Remove All
                    </RemoveAll>
                )}

                {isAnyProductInvalid && (
                    <TopWarningBox>
                        <TopWarningIcon>
                            <img src="/images/Circle-Warning.svg" alt="warning" />
                        </TopWarningIcon>
                        <TopWarningContent>
                            <TopWarningTitle>Items with expired availability</TopWarningTitle>
                            <TopWarningDescription>{topWarningMessage}</TopWarningDescription>
                            {/* TODO: Button needs to be implemented later after encpoint arrives from backend */}
                            {/* <TopWarningAction>Remove all expired items</TopWarningAction> */}
                        </TopWarningContent>
                    </TopWarningBox>
                )}

                {itemsByProduct.map((itemByProduct, productIndex) => (
                    <CartItemsByProduct
                        key={itemByProduct.productId.toString()}
                        productId={itemByProduct.productId}
                        name={itemByProduct.name}
                        productClass={itemByProduct.productClass}
                        productImagePath={itemByProduct.productImagePath}
                        itemsByFare={itemByProduct.itemsByFare}
                        {...(handleDeleteProduct && {
                            handleDeleteProduct: (pid) => handleDeleteProduct(pid),
                        })}
                        supplierName={itemByProduct.supplierName || ""}
                        startLocationName={itemByProduct.startLocationName}
                        onItemsChange={handleGroupItemsChange}
                        handleDeleteItems={handleDeleteItems}
                        originalItemsByFare={itemsByProduct[productIndex]?.itemsByFare || []}
                        is_product_latest={
                            itemByProduct.itemsByFare[0]?.itemsByGroup[0]?.items[0]
                                ?.is_product_latest
                        }
                        is_availability_latest={
                            itemByProduct.itemsByFare[0]?.itemsByGroup[0]?.items[0]
                                ?.is_availability_latest
                        }
                    />
                ))}
                {/* Hide save button in book-now flow since checkout button handles saving */}
                {!isBookNow && (
                    <CartActionsRow>
                        <BorderButton
                            $borderRadius={theme.borderRadius.small}
                            color="transparent"
                            $textColor={
                                isSubmitting ? theme.colors.gray_6 : theme.colors.button_text1
                            }
                            type="submit"
                            disabled={isSubmitting || isAnyProductInvalid}
                            onClick={async () => {
                                try {
                                    setIsSubmitting(true);
                                    await onItemsSave(items, false);
                                } catch (error) {
                                    console.error("Error saving items:", error);
                                    ErrorToast({ Message: "Failed to save items" });
                                } finally {
                                    setIsSubmitting(false);
                                }
                            }}
                        >
                            {isSubmitting ? "Updating ..." : "Update cart"}
                        </BorderButton>
                        {/* Beside Update cart rather than beside Checkout: leaving
                            to browse is the quiet alternative to the primary
                            action, and the cart keeps itself either way. */}
                        <BorderButton
                            $borderRadius={theme.borderRadius.small}
                            color="transparent"
                            $textColor={theme.colors.button_text1}
                            type="button"
                            onClick={() => navigate(PATH_HOME)}
                        >
                            Continue shopping
                        </BorderButton>
                    </CartActionsRow>
                )}
            </div>
            <div className="totalPriceContainer" ref={bottomContainerRef}>
                <CheckoutItemsActions
                    btnTitle="Checkout"
                    isSubmitting={isSubmitting}
                    disabled={isAnyProductInvalid}
                    onBtnClick={async () => {
                        try {
                            setIsSubmitting(true);
                            await onItemsSave(items, true);
                        } catch (error) {
                            console.error("Error saving items:", error);
                            ErrorToast({ Message: "Failed to save items" });
                        } finally {
                            setIsSubmitting(false);
                        }
                    }}
                    page="cart"
                    onSaveToQuote={
                        isLoggedIn && isAgent && !isQuotesPage ? handleSaveToQuote : undefined
                    }
                    onAddToExistingQuote={
                        isLoggedIn && isAgent && !isQuotesPage
                            ? handleAddToExistingQuote
                            : undefined
                    }
                    isSavingToQuote={isSavingToQuote}
                />
            </div>
        </Container>
    );
};
/**
 * The cart's secondary actions, sat under the products list.
 *
 * Spread across the full width of the list above on desktop, so the pair spans
 * it rather than bunching at one end, with the same gap above as between them.
 * Stacked full-width on a phone, where the buttons already take the whole row.
 */
const CartActionsRow = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 16px;

    ${breakpoint.breakOnlyMobile`
        flex-direction: column;
        gap: 12px;
        margin-top: 12px;
    `}
`;

export const Info = styled.div`
    background-color: ${(props) => props.theme.colors.gray_6};
    padding-bottom: 0 !important;
    margin-bottom: 16px;
    .info {
        display: flex;
        flex-direction: column;
        background-color: #fff;
        padding: 16px;
        border-radius: ${theme.borderRadius.medium};
        .main {
            display: flex;
            gap: 12px;
        }
        .img {
            position: relative;
            cursor: pointer;
            height: 64px;
            width: 64px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            img {
                height: 100%;
                width: 100%;
                object-fit: cover;
                border-radius: ${theme.borderRadius.medium};
            }
        }
        .info-container {
            display: flex;
            justify-content: space-between;
            width: 100%;
            gap: 10px;
        }
        .price-container {
            border: 1px solid #c3c9d0;
            border-radius: ${theme.borderRadius.medium};
            display: flex;
            justify-content: space-between;
            padding: 8px 20px;
            align-items: center;
            .price {
                display: flex;
                flex-direction: column;
                color: #6e7375;
                font-size: 12px;
                span {
                    color: #000;
                    font-size: 18px;
                }
            }
            .quantity {
                color: #6e7375;
                font-size: 12px;
                display: flex;
                align-items: center;
                gap: 10px;
                span {
                    font-size: 20px;
                    color: #1f2937;
                    width: 18px;
                    text-align: center;
                }
            }
        }
        .fareName {
            font-size: 18px;
            font-weight: 600;
        }
    }
    .name-container {
        display: flex;
        flex-direction: column;
        gap: 6px;
        .title {
            width: 100%;
            font-size: 14px;
            font-weight: 600;
            line-height: 23px;
            cursor: pointer;
            color: #131214;
            text-decoration: underline;
            text-underline-offset: 3px;
            @media (min-width: 375px) {
                width: 200px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            @media (min-width: 425px) {
                width: 250px;
            }
            ${breakpoint.breakLarge`
                font-size: 18px;
                width: auto;
                overflow: visible;
                text-overflow: clip;
                white-space: normal;
            `}
        }
        .sub-title {
            color: #6e7375;
            font-size: 12px;
            line-height: 14px;
            @media (min-width: 375px) {
                width: 200px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            @media (min-width: 425px) {
                width: 250px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            ${breakpoint.breakLarge`
                width: auto;
                overflow: visible;
                text-overflow: clip;
                white-space: normal;
            `}
        }
        .location {
            display: flex;
            align-items: center;
            gap: 6px;
            color: #3c3c3c;
            font-size: 12px;
            font-weight: 500;
        }
    }
`;

export const Border = styled.div`
    border-top: 1px solid #e6e9eb;
    margin: 10px 0px 14px 0px;
`;

export const Container = styled.div`
    display: flex;
    font-family: Poppins;
    gap: 20px;
    margin: 0 auto;

    ${breakpoint.breakTablet`
        width: ${(p) => p.theme.widths.wide_700};
    `}

    ${breakpoint.breakIpadPro`
        width: ${(p) => p.theme.widths.wide_900};
    `}

    ${breakpoint.breakLarge`
        width: ${(p) => p.theme.widths.wide_1110};
    `}
    
    .cartWrapper {
        position: relative;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-width: 0;
        ${breakpoint.breakOnlyMobile`
            padding-bottom: var(--bottom-container-height);
        `}
    }

    ${breakpoint.breakOnlyMobile`
        display: block;
        padding: 16px;
    `}

    .totalPriceContainer {
        flex-shrink: 0;
        ${breakpoint.breakOnlyMobile`
            position: fixed;
            bottom: 0;
            left: 0;
            background-color: white;
            z-index: 100;
            width: 100%;
        `}
    }
`;

const RemoveAll = styled.button`
    position: absolute;
    top: -30px;
    right: 20px;
    color: ${theme.colors.red};
    font-weight: 700;
    font-size: 16px;
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
`;

const SharedByBanner = styled.div`
    position: absolute;
    top: -28px;
    left: 0;
    color: ${theme.colors.gray_3};
    font-size: 14px;
    line-height: 21px;

    ${breakpoint.breakOnlyMobile`
        width: 225px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    `}

    span {
        color: ${theme.colors.black};
    }
`;

const TopWarningBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    padding: 8px 16px;
    gap: 8px;
    width: 100%;
    background: #fffbeb;
    border-radius: 8px;
    margin-bottom: 16px;
`;

const TopWarningIcon = styled.div`
    width: 24px;
    height: 24px;
    flex: none;
    order: 0;
    flex-grow: 0;
    position: relative;

    &::before {
        content: "";
        position: absolute;
        left: 16.67%;
        right: 16.67%;
        top: 16.67%;
        bottom: 16.67%;
        border: 1.5px solid #f59e0c;
        border-radius: 50%;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`;

const TopWarningContent = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0px;
    gap: 8px;
    flex: 1;
    order: 1;
    flex-grow: 1;
`;

const TopWarningTitle = styled.span`
    font-family: "Poppins";
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 150%;
    color: #b45309;
    white-space: nowrap;
`;

const TopWarningDescription = styled.span`
    font-family: "Poppins";
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 150%;
    color: #131214;
    flex: 1;
`;
